import {YascEntityMapperV3} from '@yasc/yasc-material-components';
import {BudgetCarryOverType, BudgetOveragePolicy, BudgetPricingModel, BudgetRecurrenceType, TrackerType} from '../../../enums';
import {
    ProjectBudgetReq,
    ProjectCreateReq,
    ProjectTeamMemberReq,
    ProjectUpdateReq,
} from './project.contract';
import {ProjectContractProjectReq} from './project-contract.contract';
import {ProjectMinRes, ProjectTrackerRes} from './tracker/project-tracker';
import {Project, ProjectBudgetItem, ProjectContractItem, ProjectInvoiceItem, ProjectTaskItem, ProjectTeamMember} from './project.model';
import {ApiDateAdapter} from '../../../utils/api-date-adapter';

export class ProjectMapper implements YascEntityMapperV3<ProjectMinRes, ProjectTrackerRes, Project, ProjectCreateReq, ProjectUpdateReq, number> {
    readonly modelTypes = {
        row: ProjectMinRes,
        detail: ProjectTrackerRes,
        domain: Project,
        create: ProjectCreateReq,
        patch: ProjectUpdateReq,
    };

    readonly idKey = 'id' as const;

    fromMinResponse(response: ProjectMinRes): Project {
        return new Project({
            id: response.id,
            name: response.name,
            internalName: response.internalName,
            workflowId: response.workflowId,
            currentWorkflowStatus: response.currentWorkflowStatus,
            currentStatusId: response.currentWorkflowStatus?.id ?? null,
            customer: response.customer,
            customerId: response.customer?.id ?? null,
            timesheetCustomer: response.timesheetCustomer ?? null,
            timesheetCustomerId: response.timesheetCustomer?.id ?? null,
            orderNr: response.orderNr,
            offerNr: response.offerNr,
            billingPaymentTermsDays: response.billingPaymentTermsDays ?? null,
            billingPaymentDiscountPercent: response.billingPaymentDiscountPercent ?? null,
            billingPaymentDiscountDays: response.billingPaymentDiscountDays ?? null,
            billingPreferredInvoiceFormat: response.billingPreferredInvoiceFormat ?? null,
            comment: response.comment,
            color: response.color,
            nextBill: this.toDateInputValue(response.nextBill),
            projectVolumeBrutto: response.projectVolumeBrutto ?? 0,
            projectVolumeBruttoOpen: response.projectVolumeBruttoOpen ?? 0,
            start: this.toDateInputValue(response.start),
            end: this.toDateInputValue(response.end),
            requireTasks: response.requireTasks ?? false,
            requireProjectBudget: response.requireProjectBudget ?? false,
            teamMemberCount: response.teamMemberCount ?? 0,
        });
    }

    fromDetailResponse(response: ProjectTrackerRes): Project {
        const project = this.fromMinResponse(response as unknown as ProjectMinRes);
        return project.clone({
            teamMembers: (response.teamMembers ?? []).map(member => new ProjectTeamMember({
                id: member.id,
                userId: member.user?.id ?? null,
                roleId: member.role,
                user: member.user,
                userName: [member.user?.givenName, member.user?.lastName].filter(Boolean).join(' ').trim() || member.user?.email,
            })),
            contracts: (response.contracts ?? []).map(contract => new ProjectContractItem({
                id: contract.id,
                contractNumber: contract.contractNumber,
                name: contract.name,
                start: contract.start?.slice(0, 10),
                end: contract.end?.slice(0, 10) ?? undefined,
                status: contract.status,
                document: contract.document ?? null,
                budgetCount: contract.budgetCount ?? 0,
                comment: contract.comment ?? '',
            })),
            budgets: (response.budgets ?? []).map(budget => new ProjectBudgetItem(budget)),
            tasks: (response.tasks ?? []).map(task => new ProjectTaskItem(task)),
            invoices: (response.invoices ?? []).map(invoice => new ProjectInvoiceItem(invoice)),
            teamMemberCount: response.teamMembers?.length ?? project.teamMemberCount,
        });
    }

    toCreateRequest(project: Project): ProjectCreateReq {
        return {
            name: project.name,
            internalName: project.internalName || undefined,
            type: TrackerType.Project,
            workflowId: this.requireNumber(project.workflowId, 'workflowId'),
            currentStatusId: this.requireNumber(project.currentStatusId, 'currentStatusId'),
            customerId: this.requireNumber(project.customerId, 'customerId'),
            timesheetCustomerId: this.resolveEntityId(project.timesheetCustomerId),
            orderNr: project.orderNr || undefined,
            offerNr: project.offerNr || undefined,
            billingPaymentTermsDays: project.billingPaymentTermsDays,
            billingPaymentDiscountPercent: project.billingPaymentDiscountPercent,
            billingPaymentDiscountDays: project.billingPaymentDiscountDays,
            billingPreferredInvoiceFormat: project.billingPreferredInvoiceFormat,
            comment: project.comment || undefined,
            color: project.color || undefined,
            nextBill: project.nextBill as any,
            projectVolumeBrutto: this.normalizeNumber(project.projectVolumeBrutto),
            requireTasks: project.requireTasks ?? false,
            requireProjectBudget: project.requireProjectBudget ?? false,
            start: this.toDateOnlyRequestValue(project.start),
            end: this.toDateOnlyRequestValue(project.end),
            teamMembers: project.teamMembers.map(member => this.toTeamMemberReq(member)),
            contracts: project.contracts.map(contract => this.toContractReq(contract)),
            budgets: project.budgets.map(budget => this.toBudgetReq(budget)),
            invoices: [],
            tasks: [],
            projectVolumeBruttoOpen: project.projectVolumeBruttoOpen ?? 0,
        } as ProjectCreateReq;
    }

    toPatchRequest(project: Project, _original: Project | null, _changedPaths: string[]): ProjectUpdateReq {
        return {
            id: project.id,
            name: project.name,
            internalName: this.toNullableTextRequestValue(project.internalName),
            orderNr: this.toNullableTextRequestValue(project.orderNr),
            offerNr: this.toNullableTextRequestValue(project.offerNr),
            billingPaymentTermsDays: project.billingPaymentTermsDays,
            billingPaymentDiscountPercent: project.billingPaymentDiscountPercent,
            billingPaymentDiscountDays: project.billingPaymentDiscountDays,
            billingPreferredInvoiceFormat: project.billingPreferredInvoiceFormat,
            comment: this.toNullableTextRequestValue(project.comment),
            color: this.toNullableTextRequestValue(project.color),
            projectVolumeBrutto: this.normalizeNumber(project.projectVolumeBrutto),
            customerId: this.requireNumber(project.customerId, 'customerId'),
            timesheetCustomerId: this.resolveEntityId(project.timesheetCustomerId),
            currentStatusId: this.requireNumber(project.currentStatusId, 'currentStatusId'),
            nextBill: this.toNullableDateOnlyRequestValue(project.nextBill),
            workflowId: this.requireNumber(project.workflowId, 'workflowId'),
            start: this.toNullableDateOnlyRequestValue(project.start),
            end: this.toNullableDateOnlyRequestValue(project.end),
            requireTasks: project.requireTasks ?? false,
            requireProjectBudget: project.requireProjectBudget ?? false,
            teamMembers: project.teamMembers.map(member => this.toTeamMemberReq(member)),
            contracts: project.contracts.map(contract => this.toContractReq(contract)),
            budgets: project.budgets.map(budget => this.toBudgetReq(budget)),
        } as ProjectUpdateReq;
    }

    createEmptyDomain(): Project {
        return new Project({
            requireTasks: false,
            requireProjectBudget: false,
            projectVolumeBrutto: 0,
            projectVolumeBruttoOpen: 0,
            teamMembers: [],
            contracts: [],
            budgets: [],
            tasks: [],
            invoices: [],
        });
    }

    getKey(project: Project): number {
        return project.id;
    }

    private toContractReq(contract: ProjectContractItem): ProjectContractProjectReq {
        return Object.assign(new ProjectContractProjectReq(), {
            id: contract.id ?? null,
            contractNumber: contract.contractNumber.trim(),
            name: contract.name.trim(),
            start: this.requireDateOnly(contract.start, 'contract.start'),
            end: contract.end ? this.toDateOnlyRequestValue(contract.end) : null,
            status: contract.status,
            documentId: contract.document?.id ?? null,
            comment: contract.comment?.trim() || null,
        });
    }

    private toBudgetReq(budget: ProjectBudgetItem): ProjectBudgetReq {
        const model = budget.pricingModel ?? BudgetPricingModel.TimeAndMaterial;
        const isTimeAndMaterial = model === BudgetPricingModel.TimeAndMaterial;
        const isFixedPrice = model === BudgetPricingModel.FixedPrice;
        const isCappedTimeAndMaterial = model === BudgetPricingModel.CappedTimeAndMaterial;
        const isPrepaidHours = model === BudgetPricingModel.PrepaidHours;
        const isRetainer = model === BudgetPricingModel.Retainer;

        const plannedHours = isTimeAndMaterial || isFixedPrice
            ? this.normalizeNullableNumber(budget.plannedHours)
            : null;
        const includedHours = isCappedTimeAndMaterial || isPrepaidHours || isRetainer
            ? this.normalizeNullableNumber(budget.includedHours)
            : null;
        const defaultHourlyRate = isTimeAndMaterial || isCappedTimeAndMaterial
            ? this.normalizeNullableNumber(budget.defaultHourlyRate)
            : null;
        const fixedPrice = isFixedPrice || isPrepaidHours || isRetainer
            ? this.normalizeNullableNumber(budget.fixedPrice)
            : null;
        const amountLimit = isCappedTimeAndMaterial
            ? this.normalizeNullableNumber(budget.amountLimit)
            : null;

        const supportsRecurrence = isPrepaidHours || isRetainer;
        const recurrenceType = supportsRecurrence
            ? (budget.recurrenceType ?? BudgetRecurrenceType.None)
            : BudgetRecurrenceType.None;
        const recurrenceInterval = recurrenceType === BudgetRecurrenceType.Custom
            ? this.normalizeNullableNumber(budget.recurrenceInterval)
            : null;

        const hasCapacityInterval = budget.capacityInterval !== undefined
            && budget.capacityInterval !== null
            && budget.capacityInterval !== 0;
        const carryOverType = hasCapacityInterval
            ? (budget.carryOverType ?? BudgetCarryOverType.None)
            : BudgetCarryOverType.None;
        const carryOverLimitHours = carryOverType === BudgetCarryOverType.Limited && includedHours !== null
            ? this.normalizeNullableNumber(budget.carryOverLimitHours)
            : null;
        const carryOverLimitAmount = carryOverType === BudgetCarryOverType.Limited
            ? this.normalizeNullableNumber(budget.carryOverLimitAmount)
            : null;
        const carryOverPercentage = carryOverType === BudgetCarryOverType.Percentage
            ? this.normalizeNullableNumber(budget.carryOverPercentage)
            : null;
        const carryOverExpirationPeriods = carryOverType !== BudgetCarryOverType.None
            ? this.normalizeNullableNumber(budget.carryOverExpirationPeriods)
            : null;

        const supportsOverage = isCappedTimeAndMaterial || isPrepaidHours || (isRetainer && includedHours !== null);
        const overagePolicy = supportsOverage
            ? (budget.overagePolicy ?? BudgetOveragePolicy.RequiresApproval)
            : (isTimeAndMaterial ? BudgetOveragePolicy.Billable : BudgetOveragePolicy.NotAllowed);
        const overageHourlyRate = supportsOverage && overagePolicy === BudgetOveragePolicy.Billable
            ? this.normalizeNullableNumber(budget.overageHourlyRate)
            : null;

        return Object.assign(new ProjectBudgetReq(), {
            id: budget.id ?? null,
            name: `${budget.name ?? ''}`.trim(),
            reference: this.toNullableTextRequestValue(budget.reference),
            orderNumber: this.toNullableTextRequestValue(budget.orderNumber),
            userId: this.resolveEntityId(budget.userId),
            sourceOfferId: budget.sourceOfferId ?? null,
            start: this.toDateOnlyRequestValue(budget.start),
            end: this.toNullableDateOnlyRequestValue(budget.end),
            pricingModel: model,
            currencyCode: (budget.currencyCode || 'EUR').trim().toUpperCase(),
            plannedHours,
            includedHours,
            defaultHourlyRate,
            fixedPrice,
            amountLimit,
            recurrenceType,
            recurrenceInterval,
            carryOverType,
            carryOverLimitHours,
            carryOverLimitAmount,
            carryOverPercentage,
            carryOverExpirationPeriods,
            overagePolicy,
            overageHourlyRate,
            contractId: this.resolveEntityId(budget.contractId),
            capacityInterval: budget.capacityInterval,
            capacityUnit: budget.capacityUnit,
            capacityAmount: this.normalizeNullableNumber(budget.capacityAmount),
            hoursPerDay: this.normalizeNullableNumber(budget.hoursPerDay),
            consumptionFactor: this.normalizeNullableNumber(budget.consumptionFactor),
            manualOnly: budget.manualOnly === true,
            isActive: budget.isActive !== false,
            comment: this.toNullableTextRequestValue(budget.comment),
        });
    }

    private toTeamMemberReq(member: ProjectTeamMember): ProjectTeamMemberReq {
        return new ProjectTeamMemberReq({
            userId: this.requireNumber(member.userId, 'userId'),
            roleId: this.requireEnumNumber(member.roleId, 'roleId') as any,
        });
    }

    private resolveEntityId(value: unknown): number | null {
        if (value === undefined || value === null || value === '') return null;
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = Number(value);
            return Number.isNaN(parsed) ? null : parsed;
        }

        const entity = value as {id?: unknown; value?: unknown; key?: unknown};
        return this.resolveEntityId(entity.id ?? entity.value ?? entity.key ?? null);
    }

    private requireDateOnly(value: string | null | undefined, fieldName: string): string {
        const normalized = this.toDateOnlyRequestValue(value);
        if (!normalized) throw new Error(`${fieldName} is required`);
        return normalized;
    }

    private requireNumber(value: unknown, fieldName: string): number {
        const parsed = this.resolveEntityId(value);
        if (parsed === null || parsed <= 0) {
            throw new Error(`${fieldName} is required`);
        }

        return parsed;
    }

    private requireEnumNumber(value: unknown, fieldName: string): number {
        const parsed = this.resolveEntityId(value);
        if (parsed === null || parsed < 0) {
            throw new Error(`${fieldName} is required`);
        }

        return parsed;
    }

    private normalizeNumber(value: unknown): number {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const parsed = Number(value.replace(',', '.'));
            return Number.isNaN(parsed) ? 0 : parsed;
        }

        return 0;
    }

    private normalizeNullableNumber(value: unknown): number | null {
        if (value === undefined || value === null || value === '') return null;
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value === 'string') {
            const parsed = Number(value.replace(',', '.'));
            return Number.isFinite(parsed) ? parsed : null;
        }

        return null;
    }

    private toDateInputValue(value: unknown): string | undefined {
        if (!value) return undefined;
        if (typeof value === 'string') return value.slice(0, 10);
        if (value instanceof Date) return value.toISOString().slice(0, 10);

        const raw = value as {format?: (format: string) => string; toISOString?: () => string};
        if (typeof raw.format === 'function') return raw.format('YYYY-MM-DD');
        if (typeof raw.toISOString === 'function') return raw.toISOString().slice(0, 10);

        return undefined;
    }

    private toDateOnlyRequestValue(value: unknown): string | undefined {
        if (!value) return undefined;

        if (typeof value === 'string') {
            const normalized = this.toDateInputValue(value);
            return normalized ? ApiDateAdapter.toDateOnlyKey(normalized) : undefined;
        }

        if (value instanceof Date) return ApiDateAdapter.toDateOnlyKey(value);

        const raw = value as {format?: (format: string) => string; toDate?: () => Date; toISOString?: () => string};
        if (typeof raw.format === 'function') return raw.format('YYYY-MM-DD');
        if (typeof raw.toDate === 'function') return ApiDateAdapter.toDateOnlyKey(raw.toDate());
        if (typeof raw.toISOString === 'function') return ApiDateAdapter.toDateOnlyKey(raw.toISOString());

        return undefined;
    }

    private toNullableDateOnlyRequestValue(value: unknown): string | null {
        return this.toDateOnlyRequestValue(value) ?? null;
    }

    private toNullableTextRequestValue(value: unknown): string | null {
        if (value === undefined || value === null) return null;

        const normalized = `${value}`.trim();
        return normalized.length > 0 ? normalized : null;
    }
}
